import {
  DocumentPdfPane,
  DocumentViewer,
  type DocumentPaneRenderProps,
  type DocumentViewerHighlight,
} from '@aecom-ai-engineering/react-components';
import { useGetBlobReadUrlQuery, usePackageId, useProjectId } from '@consigli/hooks';
import { FC, useMemo } from 'react';

interface RfpPdfPaneProps extends DocumentPaneRenderProps {
  initialPage: number;
  highlights: DocumentViewerHighlight[] | undefined;
}

const RfpPdfPane: FC<RfpPdfPaneProps> = ({
  active,
  caption,
  onSidebarContextChange,
  src,
  initialPage,
  highlights,
}) => {
  if (!src) {
    return (
      <section data-ds-document-viewer-main="">
        <div data-ds-document-viewer-status="">
          <span aria-hidden="true" data-ds-document-viewer-spinner="" />
          Loading document
        </div>
      </section>
    );
  }

  return (
    <DocumentPdfPane
      active={active}
      caption={caption}
      onSidebarContextChange={onSidebarContextChange}
      src={src}
      initialPage={initialPage}
      highlights={highlights}
    />
  );
};

interface RfpDeliverablePdfPanelProps {
  blobId: string;
  pageNumber: number | null;
  name: string;
  x0: number | null;
  y0: number | null;
  x1: number | null;
  y1: number | null;
  onClose: () => void;
}

export const RfpDeliverablePdfPanel: FC<RfpDeliverablePdfPanelProps> = ({
  blobId,
  pageNumber,
  name,
  x0,
  y0,
  x1,
  y1,
  onClose,
}) => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { data } = useGetBlobReadUrlQuery({ projectId, packageId, blobId });

  const hasCoordinates = x0 != null && y0 != null && x1 != null && y1 != null && pageNumber != null;

  const initialPage = pageNumber != null ? pageNumber + 1 : 1;

  const highlights = useMemo((): DocumentViewerHighlight[] | undefined => {
    if (!hasCoordinates) return undefined;
    return [{ pageNumber: pageNumber! + 1, x0: x0!, y0: y0!, x1: x1!, y1: y1! }];
  }, [hasCoordinates, pageNumber, x0, y0, x1, y1]);

  const readUrl = data?.readUrl;

  const renderers = useMemo(
    () => ({
      pdf: (props: DocumentPaneRenderProps) => (
        <RfpPdfPane {...props} initialPage={initialPage} highlights={highlights} />
      ),
    }),
    [initialPage, highlights],
  );

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={readUrl ?? ''}
      title={name}
      fileName={name}
      showSidebar={false}
      renderers={renderers}
    />
  );
};
